import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Drawer } from "antd";
import TranslateText from "../../../resuableComponents/TranslateText";
import CustomButton from "../../../resuableComponents/CustomButton";
import ErrorModal from "../../modals/errorModal/ErrorModal";
import {
  selectCurrencyCode,
  selectToatalAmount,
} from "../../../../slices/PaymentSessionDetailsSlice";
import { getStorage, numberToCurrency, getNetAmount } from "../../../../Utils/utils";
import {
  resendNativeOtp,
  resetPaymentInitiation,
  setPaymentInitiationData,
  submitNativeOtp,
  cancelTransaction,
  expireTransaction,
} from "../../../../slices/PaymentInitiationSlice";
import { resetCardFormDetails } from "../../../../slices/CardSlice";
import { getTransactionStatus } from "../../../../slices/TransactionIdSlice";
import { startAutoRefund } from "../../../../slices/AutoRefundSlice";
import { selectIsMobileLayout } from "../../../../slices/MobileLayoutSlice";
import { selectActivePaymentMethod } from "../../../../slices/PaymentButtonSlice";
import { selectSelectedIntrument } from "../../../../slices/SavedInstrumentSlice";
import { getIcon } from "../cardPayment/getCardLogos";
import { fillOtpFromInput, startWebOtpListener } from "./otpUtils";
import {
  nativeOtpEnteredEvent,
  nativeOtpScreenShownEvent,
  nativeOtpSubmittedEvent,
  nativeOtpResendEvent,
  nativeOtpCancelledEvent,
  nativeOtpBankFallbackEvent,
  nativeOtpExpiredEvent,
  nativeOtpCancelConfirmShownEvent,
} from "../../../../Utils/uiAnalytics";
import "./nativeOtp.css";

// Whole OTP page expires after this many seconds.
const PAGE_EXPIRY_SECONDS = 180;

// Max number of "Resend OTP" requests allowed per page.
const MAX_OTP_RESENDS = 3;

const formatMmSs = (totalSeconds) => {
  const safe = Math.max(0, totalSeconds);
  const mm = String(Math.floor(safe / 60)).padStart(2, "0");
  const ss = String(safe % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const NativeOtp = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const pollingRef = useRef(null);
  const expiryHandledRef = useRef(false);

  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const paymentInitiationData = useSelector((state) => state.paymentInitiation.paymentInitiationData);
  const nativeOtpLoading = useSelector((state) => state.paymentInitiation.nativeOtpLoading);
  const nativeOtpError = useSelector((state) => state.paymentInitiation.nativeOtpError);
  const nativeOtpSubmitSuccess = useSelector((state) => state.paymentInitiation.nativeOtpSubmitSuccess);
  const nativeOtpResendLoading = useSelector((state) => state.paymentInitiation.nativeOtpResendLoading);
  const nativeOtpResendError = useSelector((state) => state.paymentInitiation.nativeOtpResendError);
  const isMobileLayout = useSelector(selectIsMobileLayout);
  const currencyCode = useSelector(selectCurrencyCode);
  const amount = useSelector(selectToatalAmount);
  const issuers = useSelector((state) => state.paymentInstrumentIssuers.issuers);
  const activePaymentMethod = useSelector((state) => selectActivePaymentMethod(state));
  const selectedInstrument = useSelector((state) => selectSelectedIntrument(state));
  const isDccQuotationSelected = useSelector((state) => state.cardDetails.isDccQuotationSelected);
  const dccQuotation = useSelector((state) => state.cardDetails.dccQuotation);
  const dccMoney = dccQuotation?.dccQuotationDetails?.dccMoney?.amount;
  const issuerCurrencyDetails = useSelector((state) => state.cardDetails.issuerCurrencyDetails);
  const appliedOfferDetail = useSelector((state) => state.offers.appliedOfferDetail);
  const totalSurchargeAmount = useSelector((state) => state.surcharges.totalSurchargeAmount);
  const showTotalPaymentSummaryField = useSelector((state) => state.additionalComponents.showTotalPaymentSummaryField);
  const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);
  const headerColor = sessionDetails?.merchantDetails?.checkoutTheme?.headerColor;

  const nativeOtpActions = paymentInitiationData?.actions?.filter(
    (action) => action?.type === "nativeOtp"
  ) || [];
  const nativeOtpAction = nativeOtpActions.find((a) => a?.amount) || nativeOtpActions[0];
  const otpLength = nativeOtpAction?.maxLength || nativeOtpAction?.otpLength || 6;
  const hasBankFallback = paymentInitiationData?.actions?.some(
    (action) => action?.type === "redirect" || action?.type === "html"
  );

  const [otp, setOtp] = useState(Array(otpLength).fill(""));
  const [expirySeconds, setExpirySeconds] = useState(PAGE_EXPIRY_SECONDS);
  const isExpired = expirySeconds <= 0;
  const [resendCount, setResendCount] = useState(0);
  const resendLimitReached = resendCount >= MAX_OTP_RESENDS;
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const transactionId =
    getStorage("session", "t_id") || getStorage("session", "s_d")?.lastTransactionId;
  const additionalData = paymentInitiationData?.additionalData;
  const brand = paymentInitiationData?.paymentMethod?.brand
  const issuerCode = paymentInitiationData?.paymentMethod?.issuer
  const issuer =
    issuers.find((i) => i?.code === issuerCode)?.title || issuerCode
  const last4 = nativeOtpAction?.maskedCardNo?.slice(-4) || additionalData?.last4Digits

  const brandIcons = getIcon(brand);
  const FOOTER_BADGE_BY_BRAND = {
    visa: "/assets/footer/visa-footer.svg",
    mastercard: "/assets/footer/mastercard-footer.svg",
    rupay: "/assets/footer/rupay-footer.svg",
  };
  const footerBadgeSrc = FOOTER_BADGE_BY_BRAND[brand?.toLowerCase().replace(/[^a-z]/g, "")];
  const getAmount = (isDcc) => getNetAmount({
    isDcc, activePaymentMethod, selectedInstrument, dccMoney, amount, showTotalPaymentSummaryField, totalPaymentSummaryValue, offerAmount: appliedOfferDetail?.finalAmount,
    surchargeAmount: totalSurchargeAmount
  });
  const formattedAmount = isDccQuotationSelected
    ? `${issuerCurrencyDetails.currencyCode} ${numberToCurrency(getAmount(isDccQuotationSelected), issuerCurrencyDetails.currencyCode)}`
    : `${currencyCode} ${numberToCurrency(getAmount(), currencyCode)}`;
  const isComplete = otp.every((digit) => digit !== "");

  const focusFirstInput = useCallback(() => {
    const el = inputRefs.current[0];
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
    if (typeof el.select === "function") el.select();
  }, []);

  useEffect(() => {
    nativeOtpScreenShownEvent();
  }, []);


  useEffect(() => {
    if (nativeOtpError) {
      setOtp(Array(otpLength).fill(""));
      focusFirstInput();
    }
  }, [nativeOtpError]);

  useEffect(() => {
    if (expirySeconds <= 0) return;
    const timer = setInterval(() => {
      setExpirySeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [expirySeconds]);

  // Page expired: mark the transaction Expired on the server, fire analytics,
  // and show the "page expired" modal. Runs once.
  useEffect(() => {
    if (!isExpired || expiryHandledRef.current) return;
    expiryHandledRef.current = true;
    nativeOtpExpiredEvent();
    dispatch(
      expireTransaction({
        token: sessionDetails?.token,
        transactionId,
      })
    );
    setShowExpiredModal(true);
  }, [isExpired]);

  useEffect(() => {
    if (!nativeOtpSubmitSuccess) return;
    let isCancelled = false;
    // with auto refund on, the auto refund loader checks the status from here. Otherwise this screen polls as before.
    dispatch(startAutoRefund()).then((isAutoRefundStarted) => {
      if (isAutoRefundStarted || isCancelled) return;
      const token = sessionDetails?.token;
      pollingRef.current = setInterval(() => {
        dispatch(getTransactionStatus({ token, transactionId }));
      }, 4000);
    });
    return () => {
      isCancelled = true;
      clearInterval(pollingRef.current);
    };
  }, [nativeOtpSubmitSuccess]);

  const submitOtp = (code) => {
    if (isExpired || nativeOtpLoading || nativeOtpSubmitSuccess) {
      return;
    }
    const digits = String(code || "").replace(/\D/g, "");
    if (digits.length !== otpLength) return;
    nativeOtpSubmittedEvent();
    dispatch(submitNativeOtp({ transactionId, otp: digits }));
  };

  useEffect(() => {
    return startWebOtpListener((code) => {
      setOtp(
        Array.from({ length: otpLength }, (_, i) => code[i] || "")
      );
      if (code.length === otpLength) {
        submitOtp(code);
      } else {
        inputRefs.current[Math.min(code.length, otpLength - 1)]?.focus();
      }
    }, otpLength);
  }, []);

  const applyOtpValue = (index, rawValue) => {
    const { otp: nextOtp, focusIndex, addedDigit, filledAll } = fillOtpFromInput(
      otp,
      index,
      rawValue,
      otpLength
    );
    setOtp(nextOtp);
    if (addedDigit) {
      nativeOtpEnteredEvent();
    }
    inputRefs.current[focusIndex]?.focus();

    const bulk = String(rawValue || "").replace(/\D/g, "").length > 1;
    if (filledAll && bulk) {
      submitOtp(nextOtp.join(""));
    }
  };

  const handleChange = (index, value) => applyOtpValue(index, value);

  const handlePaste = (index, e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!/\d/.test(text)) return;
    e.preventDefault();
    applyOtpValue(index, text);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const next = [...otp];
      next[index - 1] = "";
      setOtp(next);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResend = async () => {
    if (isExpired || resendLimitReached || nativeOtpResendLoading) return;
    nativeOtpResendEvent();
    const result = await dispatch(resendNativeOtp({ transactionId }));
    if (resendNativeOtp.fulfilled.match(result)) {
      setResendCount((prev) => prev + 1);
      setOtp(Array(otpLength).fill(""));
      inputRefs.current[0]?.focus();
    }
  };

  const handleVerify = () => submitOtp(otp.join(""));

  // Close the OTP screen and drop the shopper back on an empty card form.
  const returnToCardForm = () => {
    clearInterval(pollingRef.current);
    setShowExpiredModal(false);
    setShowCancelConfirm(false);
    dispatch(resetCardFormDetails());
    dispatch(resetPaymentInitiation());
  };

  // Cancel is a two-step: open a confirmation first.
  const handleCancel = () => {
    nativeOtpCancelConfirmShownEvent();
    setShowCancelConfirm(true);
  };

  const dismissCancelConfirm = () => setShowCancelConfirm(false);

  const confirmCancel = () => {
    nativeOtpCancelledEvent();
    if (transactionId && sessionDetails?.token) {
      dispatch(
        cancelTransaction({
          token: sessionDetails.token,
          transactionId,
        })
      );
    }
    returnToCardForm();
  };

  const handlePayOnBankPage = () => {
    if (!paymentInitiationData) return;
    nativeOtpBankFallbackEvent();
    const updatedData = {
      ...paymentInitiationData,
      actions: paymentInitiationData.actions.filter(
        (action) => action?.type !== "nativeOtp"
      ),
    };
    dispatch(setPaymentInitiationData(updatedData));
  };

  const otpContent = (
    <>
      <div className="native-otp-bank-strip">
        <div>

          {footerBadgeSrc ? (
            <img
              src={footerBadgeSrc}
              alt={`${brand} verified`}
              className="native-otp-bank-badge"
            />

          ) : (
            <div className="native-otp-bank-icon">
              {brandIcons?.length > 0 ? brandIcons[0] : (brand?.slice(0, 1) || "•")}
            </div>
          )}

        </div>
        <div className="native-otp-bank-info">
          {last4 && (
            <div className="native-otp-bank-detail">
              {issuer}{" "}
              <TranslateText label="ENDING_IN">ending in</TranslateText>{" "}
              <span className="native-otp-masked">{last4}</span>
            </div>
          )}
        </div>
        <div className="native-otp-bank-amount">{formattedAmount}</div>
      </div>

      <div className="native-otp-heading">
        <TranslateText label="ENTER_OTP">Enter OTP</TranslateText>
      </div>
      <div className="native-otp-subtext">
        <TranslateText label="OTP_SENT_TO">
          {`A ${otpLength}-digit code has been sent to your registered mobile number`}
        </TranslateText>
        {nativeOtpAction?.maskedMobile && (
          <> <strong>{nativeOtpAction.maskedMobile}</strong></>
        )}
      </div>

      <div className="native-otp-inputs">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            className={`native-otp-input ${digit ? "filled" : ""} ${nativeOtpError ? "error" : ""
              }`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={otpLength}
            value={digit}
            disabled={nativeOtpLoading || isExpired || nativeOtpResendLoading}
            onChange={(e) => handleChange(index, e.target.value)}
            onPaste={(e) => handlePaste(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>
      {nativeOtpError && <div className="native-otp-error">{nativeOtpError}</div>}

      <div className="native-otp-action-row">
        <button
          type="button"
          className="native-otp-resend-btn"
          style={
            nativeOtpResendLoading || isExpired || resendLimitReached
              ? undefined
              : { color: headerColor }
          }
          disabled={
            nativeOtpResendLoading || isExpired || resendLimitReached
          }
          onClick={handleResend}
        >
          {nativeOtpResendLoading ? (
            <span className="native-otp-resend-loading">
              <span
                className="native-otp-spinner"
                style={{ borderTopColor: headerColor }}
              />
              {t("SENDING", "Sending...")}
            </span>
          ) : (
            <TranslateText label="RESEND_OTP">Resend OTP</TranslateText>
          )}
        </button>
        {hasBankFallback && (
          <span
            className="native-otp-bank-fallback-link"
            style={{ color: headerColor }}
            onClick={handlePayOnBankPage}
          >
            <TranslateText label="PAY_ON_BANK_PAGE">
              Pay on Bank's Page
            </TranslateText>
          </span>
        )}
      </div>
      {resendLimitReached && (
        <div className="native-otp-resend-note">
          <TranslateText label="RESEND_LIMIT_REACHED">
            {`You have used all ${MAX_OTP_RESENDS} resend attempts. Please retry the payment.`}
          </TranslateText>
        </div>
      )}
      {nativeOtpResendError && (
        <div className="native-otp-error">{nativeOtpResendError}</div>
      )}

      <CustomButton
        applyThemeFontSize={true}
        text={
          nativeOtpLoading || nativeOtpSubmitSuccess
            ? t("VERIFYING", "Verifying...")
            : `${t("PAY", "Pay")} ${formattedAmount}`
        }
        buttonType="primary-btn"
        block={true}
        loading={nativeOtpLoading || nativeOtpSubmitSuccess}
        disabled={
          !isComplete ||
          nativeOtpSubmitSuccess ||
          isExpired ||
          nativeOtpResendLoading
        }
        onClick={handleVerify}
      />

      <div className="native-otp-cancel" onClick={handleCancel}>
        <TranslateText label="CANCEL">Cancel</TranslateText>
      </div>

      <div className={`native-otp-expiry ${isExpired ? "expired" : ""}`}>
        {isExpired ? (
          <TranslateText label="PAGE_EXPIRED">
            This page has expired. Please retry the payment.
          </TranslateText>
        ) : (
          <>
            <TranslateText label="PAGE_EXPIRES_IN">Page expires in</TranslateText>{" "}
            <span
              className="native-otp-expiry-count"
              style={{ color: headerColor }}
            >
              {formatMmSs(expirySeconds)}
            </span>
          </>
        )}
      </div>

      <ErrorModal
        showModal={showExpiredModal}
        title={
          <TranslateText label="OTP_PAGE_EXPIRED_TITLE">
            Payment session expired
          </TranslateText>
        }
        desc={
          <TranslateText label="OTP_PAGE_EXPIRED_DESC">
            Please restart the session to continue payment.
          </TranslateText>
        }
        primaryBtnText={
          <TranslateText label="GOT_IT">Got it</TranslateText>
        }
        primaryOnClick={returnToCardForm}
      />

      <ErrorModal
        showModal={showCancelConfirm}
        title={
          <TranslateText label="OTP_CANCEL_CONFIRM_TITLE">
            Are you sure you want to cancel the payment?
          </TranslateText>
        }
        showSecondaryButton={true}
        secondaryBtnText={
          <TranslateText label="NO_STAY">No</TranslateText>
        }
        secondaryOnClick={dismissCancelConfirm}
        primaryBtnText={
          <TranslateText label="YES_CANCEL">Yes, cancel</TranslateText>
        }
        primaryOnClick={confirmCancel}
      />
    </>
  );

  if (isMobileLayout) {
    return (
      <Drawer
        title={<TranslateText label="OTP_VERIFICATION">OTP Verification</TranslateText>}
        placement="bottom"
        open={true}
        onClose={handleCancel}
        closable={true}
        maskClosable={false}
        autoFocus={false}
        afterOpenChange={() => { }}
        className="native-otp-drawer"
      >
        <div className="native-otp-body">
          {otpContent}
        </div>
      </Drawer>
    );
  }

  return (
    <div className="native-otp-overlay">
      <div className="native-otp-card-header">
        <TranslateText label="OTP_VERIFICATION">OTP Verification</TranslateText>
      </div>
      <div className="native-otp-card">
        <div className="native-otp-body">
          {otpContent}
        </div>
      </div>
    </div>
  );
};

export default NativeOtp;
